import React, { Suspense, useMemo, useState, useTransition } from 'react'
import { graphql } from 'relay-runtime'
import { useLazyLoadQuery, useMutation } from 'react-relay'
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro'
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material'
import { Archive, ChevronDown, ChevronRight, Pencil, Plus } from 'lucide-react'
import { toast } from 'react-toastify'
import {
  AccountFormDialog,
  AdminAccountInput,
} from './AccountFormDialog'
import { AccountSetupWizard } from './AccountSetupWizard'
import { AccountBrandsPanel } from './AccountBrandsPanel'
import { ConfirmArchiveDialog } from '../components/ConfirmArchiveDialog'
import { AdminQuickFilterToolbar } from '../AdminQuickFilterToolbar'
import { adminDataGridSx } from '../dataGridSx'
import { archiveIconSx } from '../styles'
import { AccountsTabQuery } from './__generated__/AccountsTabQuery.graphql'
import { AccountsTabArchiveMutation } from './__generated__/AccountsTabArchiveMutation.graphql'

export const accountsTabQuery = graphql`
  query AccountsTabQuery($input: AdminListAccountsRequest!) {
    adminListAccounts(input: $input) {
      count
      data {
        id
        name
        metaBusinessAccountId
        createdAt
        userCount
        brandCount
        ownerId
        featureFlagIds
        owner {
          id
          email
          fullName
        }
      }
    }
  }
`

const archiveAccountMutation = graphql`
  mutation AccountsTabArchiveMutation($input: DeleteRequest!) {
    archiveAccount(input: $input) {
      id
    }
  }
`

const DetailExpandIcon = () => <ChevronRight size={22} color="#014EA5" />
const DetailCollapseIcon = () => <ChevronDown size={22} color="#014EA5" />

interface AccountRow {
  id: string
  name: string
  metaBusinessAccountId: string
  createdAt: unknown
  userCount: number
  brandCount: number
  ownerId: string | null
  featureFlagIds: string[]
  owner: string
}

export const AccountsTab = () => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const [refreshKey, setRefreshKey] = useState(0)
  const [search, setSearch] = useState('')
  const [isPending, startTransition] = useTransition()
  const [wizardOpen, setWizardOpen] = useState(false)
  const [formAccount, setFormAccount] = useState<AdminAccountInput | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  const data = useLazyLoadQuery<AccountsTabQuery>(
    accountsTabQuery,
    {
      input: {
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
      },
    },
    { fetchPolicy: 'network-only', fetchKey: String(refreshKey) }
  )

  const [archiveAccount, archiving] =
    useMutation<AccountsTabArchiveMutation>(archiveAccountMutation)

  // Refresh must be a transition: it bumps the fetchKey of a network-only
  // query, which suspends this tab. Without startTransition the Suspense
  // fallback replaces the tab mid dialog-close animation, stranding a
  // half-closed MUI Dialog on screen.
  const refresh = () => startTransition(() => setRefreshKey((key) => key + 1))

  const handlePaginationChange = (model: GridPaginationModel) =>
    startTransition(() => setPaginationModel(model))

  const handleFilterChange = (model: GridFilterModel) =>
    startTransition(() => {
      setSearch((model.quickFilterValues ?? []).join(' '))
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    })

  const rows: AccountRow[] = useMemo(
    () =>
      (data.adminListAccounts.data ?? []).map((account) => ({
        id: account.id,
        name: account.name,
        metaBusinessAccountId: account.metaBusinessAccountId,
        createdAt: account.createdAt,
        userCount: account.userCount,
        brandCount: account.brandCount,
        ownerId: account.ownerId ?? null,
        featureFlagIds: [...account.featureFlagIds],
        owner: account.owner?.fullName || account.owner?.email || '—',
      })),
    [data]
  )

  const openCreate = () => setWizardOpen(true)

  const openEdit = (row: AccountRow) => {
    setFormAccount({
      id: row.id,
      name: row.name,
      metaBusinessAccountId: row.metaBusinessAccountId,
      ownerId: row.ownerId,
      featureFlagIds: row.featureFlagIds,
    })
    setFormOpen(true)
  }

  const handleArchive = () => {
    if (!archiveTarget) return
    archiveAccount({
      variables: { input: { id: archiveTarget.id } },
      onCompleted: () => {
        toast.success('Account archived.')
        setArchiveTarget(null)
        refresh()
      },
      onError: (error) => {
        console.error(error)
        toast.error(error.message || 'Unable to archive account.')
      },
    })
  }

  const columns: GridColDef<AccountRow>[] = [
    // Explicitly define the detail-panel toggle column so we control its width
    // and it doesn't render the boolean value / an overflow ellipsis.
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 56,
      renderHeader: () => <span />,
    } as GridColDef<AccountRow>,
    { field: 'name', headerName: 'Account', flex: 1, minWidth: 180 },
    { field: 'owner', headerName: 'Owner', width: 200 },
    {
      field: 'metaBusinessAccountId',
      headerName: 'Meta Business ID',
      width: 190,
    },
    { field: 'userCount', headerName: 'Users', width: 80, type: 'number' },
    { field: 'brandCount', headerName: 'Brands', width: 80, type: 'number' },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 130,
      valueFormatter: (value) =>
        value ? new Date(value as string).toLocaleDateString() : '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<AccountRow>) => (
        <div className="flex h-full w-full items-center justify-center gap-3">
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => openEdit(params.row)}
            >
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Archive">
            <IconButton
              size="small"
              sx={archiveIconSx}
              onClick={() =>
                setArchiveTarget({ id: params.row.id, name: params.row.name })
              }
            >
              <Archive size={16} />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={openCreate}
        >
          Add Account
        </Button>
      </div>

      <DataGridPro
        rows={rows}
        columns={columns}
        autoHeight
        loading={isPending}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={[25, 50, 100]}
        rowCount={data.adminListAccounts.count}
        disableRowSelectionOnClick
        disableColumnMenu
        filterMode="server"
        onFilterModelChange={handleFilterChange}
        slots={{
          toolbar: AdminQuickFilterToolbar,
          detailPanelExpandIcon: DetailExpandIcon,
          detailPanelCollapseIcon: DetailCollapseIcon,
        }}
        sx={adminDataGridSx}
        getDetailPanelHeight={() => 'auto'}
        getDetailPanelContent={({ row }: GridRowParams<AccountRow>) => (
          <Suspense
            fallback={
              <div className="p-4">
                <CircularProgress size={20} />
              </div>
            }
          >
            <AccountBrandsPanel
              accountId={row.id}
              accountName={row.name}
              accountFeatureFlagIds={row.featureFlagIds}
              onChanged={refresh}
            />
          </Suspense>
        )}
      />

      {wizardOpen && (
        <AccountSetupWizard
          onClose={() => setWizardOpen(false)}
          onCreated={refresh}
        />
      )}

      <AccountFormDialog
        open={formOpen}
        account={formAccount}
        onClose={() => setFormOpen(false)}
        onSaved={refresh}
      />

      <ConfirmArchiveDialog
        open={archiveTarget != null}
        title={`Archive '${archiveTarget?.name}'?`}
        message={`Archiving this account will also archive all of its brands and users. They will no longer be able to log in or view data, and the account's records will be hidden from the app. The data is retained in the database and can be restored later.`}
        loading={archiving}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
      />
    </div>
  )
}

export default AccountsTab
